import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import { PlayCircle, FileText, Film, X, ChevronLeft, ChevronRight } from 'lucide-react';
import BizSwitch from './BizSwitch';
import { useBizMode } from '../utils/bizMode';
import { lockBodyScroll } from '../utils/scrollLock';

// 前台"教程"按钮：弹层带两个标签页
//   业务流程（submit）   ：文字 + 多张流程图（点图放大、左右切换）
//   录屏教程（recording）：文字 + 单个视频（点开播放）
// 教程按业务（排五 / 证券）分开：弹层顶部可切换，默认展示当前所在业务的教程。
const TABS = [
  { key: 'submit',    label: '业务流程', icon: FileText },
  { key: 'recording', label: '录屏教程', icon: Film },
];

// 业务图片 URL：/uploads/tutorial/<type>/<filename>
const imageUrl = (type, filename) => `/uploads/tutorial/${type}/${filename}`;

export default function TutorialButton() {
  const [globalBiz] = useBizMode();
  const [biz, setBiz] = useState(globalBiz); // 弹层内当前查看的业务
  const [show, setShow] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('submit');
  const [lightbox, setLightbox] = useState(null); // { items, idx } 业务流程图灯箱
  const videoRef = useRef(null);

  // 视频 1.25x 倍速
  const onVideoReady = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 1.25;
      videoRef.current.defaultPlaybackRate = 1.25;
    }
  }, []);

  // 切换 tab 时重置 video 引用，让 onLoadedMetadata 重新触发
  useEffect(() => {
    if (videoRef.current) videoRef.current.load();
  }, [activeTab, biz]);

  // 弹窗打开时锁定底层页面滚动
  useEffect(() => {
    if (!show) return undefined;
    return lockBodyScroll();
  }, [show]);

  const open = useCallback(async () => {
    setBiz(globalBiz);   // 默认展示当前所在业务的教程，用户仍可手动切换
    setShow(true);
    setLoading(true);
    setData(null);
    try {
      const res = await api.get('/admin/settings/tutorial');
      const payload = res.data || {};
      // 新格式: { submit: {text, images}, recording: {text, videoUrl} }
      // 兼容老格式: { submit: {text, videoUrl} } → 归到 submit 用 videoUrl 渲染
      const norm = (src) => {
        const sub = (src && src.submit) || {};
        const rec = (src && src.recording) || {};
        return {
          submit: {
            text:  sub.text || '',
            images: Array.isArray(sub.images) ? sub.images : [],
            videoUrl: sub.videoUrl || '',
          },
          recording: {
            text:  rec.text || '',
            videoUrl: rec.videoUrl || '',
            images: Array.isArray(rec.images) ? rec.images : [],
          },
        };
      };
      // 排五在顶层（与旧版完全兼容），证券在 puzzle 下
      setData({ ...norm(payload), puzzle: norm(payload.puzzle) });
    } catch {
      const empty = { submit: { text: '', images: [], videoUrl: '' }, recording: { text: '', videoUrl: '', images: [] } };
      setData({ ...empty, puzzle: empty });
    } finally {
      setLoading(false);
    }
  }, [globalBiz]);

  const close = () => { setShow(false); setLightbox(null); };

  // 业务对应的存储槽位：排五沿用旧目录名 submit/recording，证券为 puzzle_submit/puzzle_recording
  const slot = biz === 'puzzle' ? `puzzle_${activeTab}` : activeTab;
  const currentBlock = (biz === 'puzzle' ? data?.puzzle : data)?.[activeTab] || { text: '', images: [], videoUrl: '' };
  const isRecording = activeTab === 'recording';
  const hasContent = currentBlock.text
    || (currentBlock.images && currentBlock.images.length > 0)
    || currentBlock.videoUrl;

  // 业务流程图的 URL 列表（用于灯箱）
  const imageUrls = (currentBlock.images || []).map(im => ({
    url: imageUrl(slot, im.filename),
    originalName: im.originalName
  }));

  return (
    <>
      <button className="btn btn-sm btn-outline" onClick={open} style={{ fontSize: 12 }}>
        <PlayCircle size={14} /> 教程
      </button>

      {show && createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 2147483000,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
          onClick={close}
        >
          <div
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            style={{
              background: 'var(--c-surface)',
              borderRadius: 20,
              width: '100%', maxWidth: 480,
              maxHeight: '78vh',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }}
          >
            {/* 标题栏 */}
            <div style={{
              padding: '14px 18px 0',
              textAlign: 'center',
            }}>
              <span style={{ fontSize: 17, fontWeight: 700 }}>使用教程</span>
            </div>

            {/* 业务切换：排五 / 证券 */}
            <div style={{ padding: '12px 18px 0' }}>
              <BizSwitch value={biz} onChange={setBiz} style={{ background: 'var(--c-bg)', boxShadow: 'none' }} />
            </div>

            {/* 标签头：横向 2 个 tab（移动端友好） */}
            <div style={{
              display: 'flex', gap: 6,
              padding: '10px 18px 10px',
              borderBottom: '0.5px solid var(--c-border)'
            }}>
              {TABS.map(t => {
                const Icon = t.icon;
                const active = t.key === activeTab;
                return (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    style={{
                      flex: 1, height: 40, borderRadius: 10,
                      border: 'none', cursor: 'pointer',
                      background: active ? 'var(--c-primary)' : 'var(--c-bg)',
                      color: active ? 'white' : 'var(--c-text-secondary)',
                      fontSize: 14, fontWeight: active ? 700 : 500,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      transition: 'all 0.15s'
                    }}
                  >
                    <Icon size={15} /> {t.label}
                  </button>
                );
              })}
            </div>

            {/* 内容区 */}
            <div style={{ overflowY: 'auto', padding: '16px 18px', flex: 1 }}>
              {loading ? (
                <p style={{ textAlign: 'center', padding: '28px 0', color: 'var(--c-text-secondary)' }}>加载中...</p>
              ) : !hasContent ? (
                <p style={{ textAlign: 'center', padding: '28px 0', color: 'var(--c-text-secondary)' }}>
                  管理员暂未配置{biz === 'puzzle' ? '证券' : '排五'}{TABS.find(t => t.key === activeTab)?.label}
                </p>
              ) : (
                <>
                  {currentBlock.text && (
                    <div style={{
                      fontSize: 14, lineHeight: 1.8, color: 'var(--c-text)',
                      whiteSpace: 'pre-wrap',
                      marginBottom: (isRecording
                        ? (currentBlock.videoUrl ? 14 : 0)
                        : (currentBlock.images && currentBlock.images.length) ? 14 : 0),
                    }}>
                      {currentBlock.text}
                    </div>
                  )}
                  {/* 业务流程：多张图 */}
                  {!isRecording && currentBlock.images && currentBlock.images.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {currentBlock.images.map((im, idx) => (
                        <img
                          key={im.filename}
                          src={imageUrl(slot, im.filename)}
                          alt={im.originalName || `流程图 ${idx + 1}`}
                          onClick={() => setLightbox({ items: imageUrls, idx })}
                          style={{
                            width: '100%',
                            borderRadius: 10,
                            border: '1px solid var(--c-border)',
                            cursor: 'zoom-in',
                            display: 'block',
                            background: 'var(--c-bg)'
                          }}
                        />
                      ))}
                    </div>
                  )}
                  {/* 录屏教程：单个视频 */}
                  {isRecording && currentBlock.videoUrl && (
                    <video
                      key={`${biz}-${activeTab}`}
                      ref={videoRef}
                      src={currentBlock.videoUrl}
                      controls
                      playsInline
                      preload="metadata"
                      defaultPlaybackRate={1.25}
                      onLoadedMetadata={onVideoReady}
                      style={{
                        width: '100%',
                        maxWidth: 320,
                        maxHeight: '40vh',
                        borderRadius: 12,
                        background: '#000',
                        display: 'block',
                        margin: '0 auto',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                </>
              )}
            </div>

            {/* 底部固定关闭 */}
            <div style={{ padding: '10px 18px 16px', borderTop: '0.5px solid var(--c-border)' }}>
              <button className="btn btn-primary" onClick={close} style={{ width: '100%', height: 46 }}>
                关闭
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 灯箱：点击图片全屏预览，左右切换 */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 2147483600,
            background: 'rgba(0,0,0,0.92)', display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 16
          }}
        >
          <button
            onClick={() => setLightbox(null)}
            style={{
              position: 'absolute', top: 'calc(16px + env(safe-area-inset-top, 0px))', right: 'calc(16px + env(safe-area-inset-right, 0px))',
              background: 'rgba(255,255,255,0.1)', border: 'none',
              borderRadius: '50%', width: 36, height: 36,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <X size={18} color="white" />
          </button>

          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>
            {lightbox.idx + 1} / {lightbox.items.length}
          </div>

          <img
            src={lightbox.items[lightbox.idx].url}
            alt=""
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '92vw', maxHeight: '72vh', objectFit: 'contain', borderRadius: 12 }}
          />

          {lightbox.items[lightbox.idx].originalName && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 10, maxWidth: '90vw', textAlign: 'center' }}>
              {lightbox.items[lightbox.idx].originalName}
            </div>
          )}

          {lightbox.items.length > 1 && (
            <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
              <button
                onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, idx: (l.idx - 1 + l.items.length) % l.items.length })); }}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10,
                  padding: '8px 20px', color: 'white', cursor: 'pointer', fontSize: 14,
                  display: 'flex', alignItems: 'center', gap: 6
                }}
              ><ChevronLeft size={16} /> 上一张</button>
              <button
                onClick={e => { e.stopPropagation(); setLightbox(l => ({ ...l, idx: (l.idx + 1) % l.items.length })); }}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10,
                  padding: '8px 20px', color: 'white', cursor: 'pointer', fontSize: 14,
                  display: 'flex', alignItems: 'center', gap: 6
                }}
              >下一张 <ChevronRight size={16} /></button>
            </div>
          )}
        </div>
      )}
    </>
  );
}